import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowLeft } from "lucide-react";
import { Btn } from "@/components/Btn";
import WaveMark from "@/components/WaveMark";
import { PACKAGES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Field = ({ label, name, value, onChange, error, type = "text", required }) => (
  <div>
    <label htmlFor={name} className="text-[11px] uppercase tracking-label text-ink/50">
      {label}{!required && <span className="text-ink/30"> (optional)</span>}
    </label>
    <input id={name} name={name} type={type} value={value} onChange={onChange} data-testid={`start-${name}`}
      className={`mt-2 w-full border-b bg-transparent pb-2.5 text-lg text-ink focus:outline-none ${error ? "border-red-500" : "border-ink/25 focus:border-ink"}`} />
    {error && <p className="mt-1 text-xs text-red-600" data-testid={`error-${name}`}>{error}</p>}
  </div>
);

const STEPS = ["Choose your service", "Your business", "Your project", "Confirmation"];

export default function StartProject() {
  const [params] = useSearchParams();
  const preset = params.get("package");
  const [pkg, setPkg] = useState(preset && PACKAGES[preset] ? preset : "");
  const [step, setStep] = useState(preset && PACKAGES[preset] ? 1 : 0);
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", current_website: "", message: "", referral_code: "", referrer: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const current = STEPS[step];
  const price = pkg ? PACKAGES[pkg].price : 0;

  const validateBusiness = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!EMAIL_RE.test(form.email)) e.email = "Enter a valid email.";
    setErrors(e); return Object.keys(e).length === 0;
  };
  const validateProject = () => {
    const e = {};
    if (!form.message.trim()) e.message = "Please tell us a little about your project.";
    setErrors(e); return Object.keys(e).length === 0;
  };

  const next = () => {
    if (current === "Your business" && !validateBusiness()) return;
    if (current === "Your project" && !validateProject()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setBusy(true); setErrors({});
    try {
      await axios.post(`${API}/project-request`, { package: pkg, ...form });
      setDone(true);
    } catch (e) {
      setBusy(false); setErrors({ submit: "Something went wrong. Please try again." });
    }
  };

  if (done) {
    return (
      <section className="flex min-h-screen items-center bg-ink text-white">
        <div className="mx-auto max-w-[760px] px-6 py-40 text-center md:px-12" data-testid="start-success">
          <div className="mx-auto flex h-16 w-16 items-center justify-center border border-champagne text-champagne"><Check size={30} strokeWidth={1.5} /></div>
          <h1 className="mt-10 font-serif text-5xl tracking-tight md:text-6xl">Thanks — we've got it.</h1>
          <p className="mt-6 text-xl text-champagne">Your Wave Eleven project starts here.</p>
          <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-white/70">We'll be in touch shortly to take you through the next steps.</p>
          <div className="mt-10 flex justify-center"><Btn to="/" variant="gold" testid="start-success-home">Back to home</Btn></div>
          <WaveMark width={120} stroke="rgba(255,255,255,0.15)" className="mx-auto mt-16" />
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-paper text-ink">
      <div className="mx-auto max-w-[840px] px-6 pt-40 pb-24 md:px-12 md:pt-48">
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-label text-ink/45">Start a Project</p>
          <p className="text-[11px] uppercase tracking-label text-ink/45" data-testid="start-step-indicator">Step {step + 1} / {STEPS.length} · {current}</p>
        </div>
        <div className="mt-4 h-px w-full bg-ink/10"><div className="h-full bg-champagne transition-all duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="mt-12">

            {current === "Choose your service" && (
              <div data-testid="start-step-service">
                <h1 className="font-serif text-4xl tracking-tight md:text-5xl">Choose your service.</h1>
                <div className="mt-10 grid gap-4 sm:grid-cols-2">
                  {Object.entries(PACKAGES).map(([k, v]) => (
                    <button key={k} data-testid={`pick-${k}`} onClick={() => { setPkg(k); setStep(1); }}
                      className={`flex items-center justify-between border p-6 text-left transition-colors duration-300 ${pkg === k ? "border-champagne bg-paper-cream" : "border-ink/15 hover:border-ink/50"}`}>
                      <span className="text-[11px] uppercase tracking-label">{v.label}</span>
                      <span className="font-serif text-3xl">£{v.price.toLocaleString()}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {current === "Your business" && (
              <div data-testid="start-step-business">
                <h1 className="font-serif text-4xl tracking-tight md:text-5xl">Your business.</h1>
                <div className="mt-10 grid gap-8 sm:grid-cols-2">
                  <Field label="Name" name="name" value={form.name} onChange={update} error={errors.name} required />
                  <Field label="Business name" name="business" value={form.business} onChange={update} />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={update} error={errors.email} required />
                  <Field label="Telephone" name="phone" value={form.phone} onChange={update} />
                  <div className="sm:col-span-2"><Field label="Current website" name="current_website" value={form.current_website} onChange={update} /></div>
                  <div className="sm:col-span-2"><Field label="Referral code" name="referral_code" value={form.referral_code} onChange={update} /></div>
                  {form.referral_code.trim().toUpperCase() === "WAVE200" && (
                    <div className="sm:col-span-2" data-testid="referrer-field">
                      <Field label="Who referred you?" name="referrer" value={form.referrer} onChange={update} />
                      <p className="mt-1 text-xs text-ink/50">Enter their name or business name so we know who to thank.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {current === "Your project" && (
              <div data-testid="start-step-project">
                <h1 className="font-serif text-4xl tracking-tight md:text-5xl">Your project.</h1>
                <label htmlFor="message" className="mt-10 block text-[11px] uppercase tracking-label text-ink/50">Tell us a little about your business and what you'd like us to create.</label>
                <textarea id="message" name="message" rows={6} value={form.message} onChange={update} data-testid="start-message"
                  className={`mt-3 w-full resize-none border-b bg-transparent pb-3 text-lg text-ink focus:outline-none ${errors.message ? "border-red-500" : "border-ink/25 focus:border-ink"}`} />
                {errors.message && <p className="mt-1 text-xs text-red-600" data-testid="error-message">{errors.message}</p>}
                <p className="mt-5 text-sm leading-relaxed text-ink/55">Don't worry about getting everything right — we'll go through the details with you after you start.</p>
              </div>
            )}

            {current === "Confirmation" && (
              <div data-testid="start-step-confirm">
                <h1 className="font-serif text-4xl tracking-tight md:text-5xl">Confirm & start.</h1>
                <div className="mt-10 border border-ink/15 bg-paper-cream p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] uppercase tracking-label text-ink/50">Selected service</p>
                      <p className="mt-2 text-[11px] uppercase tracking-label">{PACKAGES[pkg].label}</p>
                    </div>
                    <span className="font-serif text-4xl">£{price.toLocaleString()}</span>
                  </div>
                </div>
                <p className="mt-6 text-sm leading-relaxed text-ink/55">No payment is taken now. Start your project and we'll be in touch to take you through the next steps.</p>
                {errors.submit && <p className="mt-4 text-sm text-red-600" data-testid="error-submit">{errors.submit}</p>}
                <div className="mt-8"><Btn onClick={submit} disabled={busy} variant="primary" testid="start-submit">{busy ? "Sending…" : "Start my Project"}</Btn></div>
              </div>
            )}

          </motion.div>
        </AnimatePresence>

        <div className="mt-12 flex items-center justify-between">
          {step > 0 ? (<button onClick={back} data-testid="start-back" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-label text-ink/60 hover:text-ink"><ArrowLeft size={15} /> Back</button>) : <span />}
          {current !== "Confirmation" && current !== "Choose your service" && (<Btn onClick={next} variant="primary" testid="start-next">Continue</Btn>)}
        </div>
      </div>
    </section>
  );
}
